import {
	asyncScheduler,
	buffer,
	concatMap,
	filter,
	fromEvent,
	map,
	of,
	Subject,
	take,
	tap,
	throttleTime,
	timeout,
	zip,
} from 'rxjs'
import { SchmancyTeleportation } from './teleport.component'

export type WhereAreYouRickyEvent = CustomEvent<{
	id: string
	callerID: number
}>

export const WhereAreYouRicky = 'whereAreYouRicky'

export type HereMortyEvent = CustomEvent<{
	component: SchmancyTeleportation
}>

export type FLIP_REQUEST = {
	from: {
		rect: DOMRect
		element?: HTMLElement
	}
	to: {
		rect: DOMRect
		element: HTMLElement
	}
	stagger?: number
	host: HTMLElement
}
export const HereMorty = 'hereMorty'

class Teleportation {
	activeTeleportations = new Map<string, DOMRect>()
	flipRequests = new Subject<FLIP_REQUEST>()

	constructor() {
		this.flipRequests
			.pipe(
				// Batches requests arriving within 1ms of the first. The singleton lives from
				// import, so a timer is scheduled only while a request is pending.
				buffer(this.flipRequests.pipe(throttleTime(1, asyncScheduler, { leading: false, trailing: true }))),
				map(requests =>
					requests.map(({ from, to, host }, i) => ({
						from,
						to,
						host,
						i,
					})),
				),
				concatMap(requests => zip(requests.map(request => of(this.flip(request))))),
			)
			.subscribe()
	}

	find = (component: SchmancyTeleportation) => {
		return zip([
			fromEvent<HereMortyEvent>(window, HereMorty).pipe(
				filter(
					e =>
						!!e.detail.component.uuid &&
						!!component.id &&
						e.detail.component.id === component.id &&
						e.detail.component.uuid !== component.uuid,
				),
				map(e => e.detail.component),
				take(1),
			),
			of(component).pipe(
				tap(() => {
					window.dispatchEvent(
						new CustomEvent<WhereAreYouRickyEvent['detail']>(WhereAreYouRicky, {
							detail: {
								id: component.id,
								callerID: component.uuid,
							},
						}),
					)
				}),
			),
		]).pipe(
			map(([component]) => component),
			timeout(0),
		)
	}

	flip = (request: {
		from: {
			rect: DOMRect
		}
		to: {
			element: HTMLElement
			rect: DOMRect
		}
		host: HTMLElement
		i: number
	}) => {
		const { from, to } = request

		const originalZIndex = to.element.style.zIndex
		to.element.style.transformOrigin = 'top left'
		to.element.style.setProperty('visibility', 'visible')
		to.element.style.zIndex = '1000'

		const startX = from.rect.left - to.rect.left
		const startY = from.rect.top - to.rect.top
		const startScaleX = from.rect.width / to.rect.width
		const startScaleY = from.rect.height / to.rect.height

		const keyframes: Keyframe[] = [
			{
				transform: `translate(${startX}px, ${startY}px) scale(${startScaleX}, ${startScaleY})`,
			},
			{
				transform: 'translate(0, 0) scale(1, 1)',
			},
		]

		const animation = to.element.animate(keyframes, {
			duration: 250,
			delay: 10,
			easing: 'cubic-bezier(0.455, 0.03, 0.515, 0.955)', // approximates the 'inOutQuad' easing curve
		})

		animation.onfinish = () => {
			to.element.style.zIndex = originalZIndex
			to.element.style.transformOrigin = ''
		}
	}
}

export const teleport = new Teleportation()
export default teleport
